import { PaginationProps } from 'antd';
const MarkerIcon = () => (
  <svg width="14" height="17" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="addr-listing-marker-grad" x1="11.5" y1="-2" x2="-5" y2="22.5" gradientUnits="userSpaceOnUse">
        <stop stopColor="var(--s247-icon-start, #AC2B90)"/>
        <stop offset="1" stopColor="var(--s247-icon-end, #EE5B33)"/>
      </linearGradient>
    </defs>
    <path d="M7.5 0.791687C9.38967 0.791687 11.2019 1.54235 12.5381 2.87855C13.8743 4.21475 14.625 6.02702 14.625 7.91669C14.625 10.8664 12.3767 13.9571 7.975 17.2584C7.83797 17.3611 7.67129 17.4167 7.5 17.4167C7.32871 17.4167 7.16203 17.3611 7.025 17.2584C2.62333 13.9571 0.375 10.8664 0.375 7.91669C0.375 6.02702 1.12567 4.21475 2.46186 2.87855C3.79806 1.54235 5.61033 0.791687 7.5 0.791687ZM7.5 5.54169C6.87011 5.54169 6.26602 5.79191 5.82062 6.23731C5.37522 6.68271 5.125 7.2868 5.125 7.91669C5.125 8.54658 5.37522 9.15067 5.82062 9.59607C6.26602 10.0415 6.87011 10.2917 7.5 10.2917C8.12989 10.2917 8.73398 10.0415 9.17938 9.59607C9.62478 9.15067 9.875 8.54658 9.875 7.91669C9.875 7.2868 9.62478 6.68271 9.17938 6.23731C8.73398 5.79191 8.12989 5.54169 7.5 5.54169Z" fill="url(#addr-listing-marker-grad)"/>
  </svg>
);
import PlusIcon from 'Components/Icons/PlusIcon';
import search from 'Assets/icon/search.svg';
import ButtonClick from 'Components/ButtonClick';
import InputText from 'Components/InputText';
import Paginate from 'Components/Paginate';
import Section from 'Components/Section';
import { debounce, omitBy } from 'lodash';
import { AddressList } from 'Pages/AddressBook/AddressListing/components';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DTOAddress } from 'Services/v1/address-book/dto';
import * as addressBook from 'Stores/address-book';
import flightSlice from 'Stores/flights';
import { getResetLocation } from 'Stores/locations';
import {
  orderBillSelectors,
  updateReceiverAddress,
  updateSenderAddress
} from 'Stores/order-bill';
import { PAGESIZE, PAGESIZE_UNLIMIT, VIETNAM_COUNTRY_CODE } from 'Utilities/constants';
import { classPrefix } from 'Utilities/global';
import { AddressPathEnum, MainPageEnum } from '../../../routes/enums';

import { useLoadingOverlay } from 'Components/LayoutUtilities';
import './index.scss';

interface AddressListingProps {
	isSelectable?: boolean;
}

interface SelectAddressStoreMapping {
	[key: string]: {
		updateAction: any
		fieldName: string
		cleanUp: () => void
	}
}

const AddressListing = (props: AddressListingProps) => {
	const { isSelectable = false } = props;
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [keyword, setKeyword] = useState('');
	const [isSearching, setIsSearching] = useState(false);
	const addressListing = useSelector(addressBook.addressBookSelectors.get).list;
	const fetchingStatus = useSelector(addressBook.addressBookSelectors.get).fetching;
	const addressMeta = useSelector(addressBook.addressBookSelectors.get).meta;
	const urlNavigationReturn = useSelector(addressBook.addressBookSelectors.get).urlNavigationReturn;
	const orderBillState = useSelector(orderBillSelectors.get);
	const accessibleLocation = useSelector(addressBook.addressBookSelectors.get).accessibleLocation;
	const accessibleLocationIds = accessibleLocation ? Object.keys(accessibleLocation) : [];
	const [searchParams] = useSearchParams();
	const page = parseInt(searchParams.get('page') || '1');
	const {orderType, addressType} = useParams();
	const {startLoadingOverlay, stopLoadingOverlay} = useLoadingOverlay();
	const isC2C = orderType === 'c2c';
  const limit = isC2C ? PAGESIZE_UNLIMIT : PAGESIZE;

	const storeMapping:SelectAddressStoreMapping = {
		sender: {
			updateAction: updateSenderAddress,
			fieldName: 'senderAddress',
			cleanUp: () => {
				dispatch(flightSlice.actions.departureAirportsActions.reset())
				dispatch(flightSlice.actions.arrivalAirportsActions.reset())
			}
		},
		receiver: {
			updateAction: updateReceiverAddress,
			fieldName: 'receiverAddress',
			cleanUp: () => {
				dispatch(flightSlice.actions.arrivalAirportsActions.reset())
			}
		},
	} as any;

	// @ts-ignore
	const selectedAddress = isSelectable ? orderBillState[storeMapping[addressType].fieldName] : undefined;
	const nextSearchParams = useMemo(() => {
		const searchParamsPayload = omitBy<any>({
			addressType,
			orderType,
			page: page.toString()
		}, i => !i)
		return createSearchParams(searchParamsPayload)
	}, [addressType, orderType, page])

	const onHandleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		const key = event.target.value.toLowerCase();
		setKeyword(event.target.value);
		setIsSearching(true);
		debounceValue(key);
	};

	const debounceValue = useCallback(
		debounce((nextValue) => fetchValue(nextValue), 500),
		[addressListing],
	);

	const fetchValue = (search_text: string) => {
		dispatch(addressBook.getAddressBook({ limit, page: 1, search_text }));
		setIsSearching(false);
	};

	useEffect(() => {
		if (isC2C && addressType === 'sender') {
			dispatch(addressBook.getAccessibleLocation())
		}
		dispatch(getResetLocation());
	}, [isC2C, addressType]);

	const goToSearchAddress = () => {
		navigate({
			pathname: AddressPathEnum.AddressFormCreate,
			search: nextSearchParams.toString()
		});
	}

	const onEditAddress = (item: DTOAddress) => {
		dispatch(addressBook.getAddressBookDetail(item));
		navigate({
			pathname: AddressPathEnum.AddressFormUpdate.replace(':id', item.id),
			search: nextSearchParams.toString(),
		});
	};

	const saveAddressToStore = (item: DTOAddress) => {
		const storeData = storeMapping[
			addressType as unknown as keyof typeof storeMapping
		]
		dispatch(storeData.updateAction(item));
		storeData.cleanUp();
	}

	// const cargoSelectAddress = (item: DTOAddress) => {
	// 	saveAddressToStore(item);
	// 	navigate(MainPageEnum.OrderBill);
	// }

	useEffect(() => {
		if(fetchingStatus.listingStatus === 'pending') {
			startLoadingOverlay();
			return () => stopLoadingOverlay();
		}
		stopLoadingOverlay();
	}, [fetchingStatus])

	const onSelectAddress = (item: DTOAddress) => {
		saveAddressToStore(item);
		if(isC2C) {
			return navigate(`/${MainPageEnum.C2cOrderPayment}`);
		}

		navigate(`/${MainPageEnum.OrderBill}`);
	}

	const renderData = () => {
		if (isC2C) {
			if(addressType === 'receiver') return <AddressList isSelectable={isSelectable} onEdit={onEditAddress} selectedItem={selectedAddress} onSelect={onSelectAddress} data={addressListing}/>
			const validAddresses = addressListing.filter(address => accessibleLocationIds.includes(address.province) || address.country?.toString() !== VIETNAM_COUNTRY_CODE)
			const invalidAddresses = addressListing.filter(address => !accessibleLocationIds.includes(address.province) && address.country?.toString() === VIETNAM_COUNTRY_CODE)

			return <>
				<AddressList isSelectable={isSelectable} onEdit={onEditAddress} selectedItem={selectedAddress} onSelect={onSelectAddress} data={validAddresses}/>
				<AddressList onEdit={onEditAddress} disabled={true} data={invalidAddresses} title={'message_do_not_support_shipping'} subTitle={'message_do_not_support_shipping_by_location'}/>
			</>
		} else {
			return <AddressList isSelectable={isSelectable} onEdit={onEditAddress} selectedItem={selectedAddress} onSelect={onSelectAddress} data={addressListing}/>
		}
	};

	const renderPagination = () => {
		if (addressMeta.pagination && addressMeta.pagination.total) {
			return (
				<Paginate
					total={addressMeta.pagination.total}
					size={addressMeta.pagination.perPage}
					current={page}
					onPageChange={onPageChange}
				/>
			);
		}
	};

	useEffect(() => {
		dispatch(addressBook.getAddressBook({ limit, page: isC2C ? 1 : page }))
	}, [page, isC2C])

	const onPageChange: PaginationProps['onChange'] = (pageNumber, pageSize) => {
		navigate(`?page=${pageNumber}`);
	};

	const compClass = `${classPrefix}-address-listing`;

	return (
		<>
			<Section
				title={t('title_saved_address')}
				icon={<MarkerIcon />}
			>
				<div className={`${compClass}-container pb-4`}>
					<div
						className={`${compClass}-address-search-bar d-flex mt-4 justify-content-around`}
					>
						<InputText
							className={`${classPrefix}-address-search-input`}
							value={keyword}
							name="search-box"
							placeholder={t('placeholder_search_address')}
							onChange={onHandleSearch}
							addonBefore={<img src={search} width="18" />}
							onKeyUp={() => onHandleSearch}
						/>
					</div>
					<div className="add-address pt-10 px-4">
						<div className={`${classPrefix}-btn-container`}>
							<ButtonClick
								variant="default"
								icon={<PlusIcon width={14} className="mr-2" />}
								onClick={goToSearchAddress}
							>
								{t('buttons_add_address')}
							</ButtonClick>
						</div>
						<div>{renderData()}</div>
					</div>
				</div>
				<Outlet />
			</Section>
			{ !isC2C && renderPagination() }
		</>
	);
};

export default AddressListing;
